import React, { useEffect } from 'react';
import qs from 'qs';
import { orderBy, groupBy, uniq, maxBy, omit, isEmpty, sum, sumBy, get, keyBy, omitBy, pick, isUndefined, max } from 'lodash';
import { format as formatDate, eachDayOfInterval, eachWeekOfInterval, eachMonthOfInterval, addDays, addMonths, startOfDay, endOfDay, startOfMonth, endOfMonth, differenceInDays } from 'date-fns';
import { Button, Badge, Nav, NavItem, NavLink, } from 'reactstrap';
import numeral from 'numeral';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { ResponsiveContainer, BarChart, Bar, LineChart, CartesianGrid, XAxis, YAxis, Tooltip, Legend, Line, } from 'recharts';
import ellipsis from 'text-ellipsis';

import { currencies, prefectures, routes, colors, } from '../shared/config';
import { chart, } from '../shared/models/order';
import useQueryParams from './hooks/useQueryParams';
import QuerySelector from './QuerySelector';
import QueryDateSelector from './QueryDateSelector';

const { entries } = Object;
const { metrics, metricOptions, dateTypes, dateTypeOptions, } = chart;

export default function ComparisonCharts(props) {
  const { filteredRows, filteredComparedRows = [], startOn, endOn, comparedStartOn, comparedEndOn, productsById, history, location, activeProductTypes } = props;
  const dimensions = props.dimensions ?? chart.dimensions;
  const queryParams = useQueryParams();
  const {
    metric = 'count',
  } = queryParams;
  const groups = [
    { rangeName: [comparedStartOn, comparedEndOn].map(_ => _ ? formatDate(_, 'yyyy/MM/dd') : '').join(' - '), rows: filteredComparedRows, },
    { rangeName: [startOn, endOn].map(_ => formatDate(_, 'yyyy/MM/dd')).join(' - '), rows: filteredRows, },
  ];
  const cardWidth = 1200;
  const cardHeight = cardWidth * 0.4;
  const chartWidth = cardWidth * 0.95;
  const chartHeight = chartWidth * 0.35;
  const metricsWithFilter = metrics(productsById, activeProductTypes);
  const charts = {
    all: {
      title: '全体',
      data: [
        groups.reduce((x, y) => ({
          ...x,
          [y.rangeName]: metricsWithFilter[metric].value(y.rows),
        }), { x: '全体', }),
      ],
      ys: groups.map(_ => ({
        label: _.rangeName,
      })),
    },
    ...entries(dimensions).reduce((x, [dimension, { label, computeItems, value, }]) => {
      const items = computeItems([...filteredComparedRows, ...filteredRows]);
      const rowsGroupedByDimension = [filteredComparedRows, filteredRows].map(_ => groupBy(_, value));
      const data = orderBy(items.map((item) => {
        return groups.reduce((x, y, i) => ({
          ...x,
          [y.rangeName]: metricsWithFilter[metric].value(rowsGroupedByDimension[i][item.value] || []),
        }), { x: item.label || '(なし)', });
      }), [_ => _[groups[0].rangeName], _ => _[groups[1].rangeName]], ['desc', 'desc']).slice(0, 10);
      return {
        ...x,
        [dimension]: {
          title: label,
          data: data,
          ys: groups.map(_ => ({
            label: _.rangeName,
          })),
        },
      };
    }, {}),
  };
  const onSelect = (dates) => {
    history.replace(`${location.pathname}?${qs.stringify({ ...queryParams, comparedStartOn: formatDate(dates[0], 'yyyy-MM-dd'), comparedEndOn: formatDate(dates[1], 'yyyy-MM-dd'), })}`);
  };
  const buttons = [
    { label: '1年前', monthsAgo: 12, },
    { label: '半年前', monthsAgo: 6, },
    { label: '前月', monthsAgo: 1, },
  ].map(_ => ({ label: _.label, dates: () => [addMonths(startOn, -_.monthsAgo), addMonths(endOn, -_.monthsAgo)] }));

  return (
    <div className="mt-3">
      <div className='d-flex align-items-end gap-2 justify-content-end'>
        <div>
          {
            buttons.map((button, i) => {
              const dates = button.dates();
              return (
                <Button key={i} size="sm" color="link" onClick={onSelect.bind(null, dates)}>
                  {button.label}
                </Button>
              );
            })
          }
        </div>
        <QueryDateSelector
          paramName='comparedStartOn'
          label='比較開始日'
          history={history}
          location={location}
          defaultValue={comparedStartOn}
          style={{ width: 150 }}
        />
        <QueryDateSelector
          paramName='comparedEndOn'
          label='比較終了日'
          history={history}
          location={location}
          defaultValue={comparedEndOn}
          style={{ width: 150 }}
        />
        <QuerySelector
          paramName='metric'
          options={metricOptions(productsById, activeProductTypes)}
          label=''
          defaultValue={'bodysCount'}
          isClearable={false}
        />
      </div>
      <div className="mt-3 d-flex flex-wrap justify-content-around gap-3">
        {
          entries(charts).map(([name, { title, data, ys, }]) => {
            return (
              <div key={name} className="card" style={{ width: cardWidth, height: cardHeight }}>
                <div className="card-header d-flex justify-content-between">
                  <div className="flex-fill">
                    {title}
                  </div>
                </div>
                <div className="card-body d-flex justify-content-center align-items-center">
                  <div>
                    <BarChart width={chartWidth} height={chartHeight} data={data}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="x" tick={{ fontSize: 11 }} interval={0} tickFormatter={_ => ellipsis(_, 10)} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={_ => numeral(_).format(metricsWithFilter[metric].format())} tick={{ fontSize: 14.5 }} />
                      <Tooltip />
                      <Legend />
                      {
                        ys.map(({ label }, i) => {
                          const isMain = i === 1;
                          return (
                            <Bar
                              key={label}
                              dataKey={label}
                              fill={colors[i]}
                              formatter={(v, _, { payload }) => {
                                const comparedValue = payload[groups[0].rangeName];
                                const contentForMain = numeral((v - comparedValue) / comparedValue).format('+0,0.0%');
                                // return `${numeral(v).format(metrics(productsById)[metric].format())} (シェア率: ${numeral(v / sum(Object.values(omit(payload, 'x')))).format('0,0.0%')}) ${isMain ? contentForMain : ''}`;
                                return `${numeral(v).format(metricsWithFilter[metric].format())} ${isMain ? `(${contentForMain})` : ''}`;
                              }}
                            />
                          );
                        })
                      }
                    </BarChart>
                  </div>
                </div>
              </div>
            )
          })
        }
      </div>
    </div>
  );
}
